import { useRef } from 'react';
import { Box, Container, Typography, alpha, Stack, Avatar } from '@mui/material';
import { motion } from 'framer-motion';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { SECTION_PY } from 'const/section';

const VoiceSection = () => {
  const primaryColor = '#2aa1af';
  const scrollRef = useRef<HTMLDivElement>(null);

  // ！！ 公開前に必ず実在のお客様の声へ差し替えること ！！
  // 以下は現在の訴求軸(加算取得・減算対策・書類負担・属人化)に沿って用意した下書き。
  // 実際に利用者から得ていないコメントをそのまま掲載すると、景品表示法の優良誤認や
  // ステルスマーケティング規制に抵触するおそれがある。ヒアリングが済むまでは、
  // このセクション自体を非表示にするか「想定される活用イメージ」と明示して掲載すること。
  const voiceData = [
    {
      facility: '放課後等デイサービス A施設（2教室）',
      role: '代表取締役',
      title: '専門的支援加算、ずっと「いつかやる」のままでした',
      comment: '要件自体は研修で聞いて知っていたんです。ただ、実施計画書と毎回の記録をどう残すかが決まらなくて、二年近く手つかずでした。書類のたたき台が出るようになってから、ようやく職員に「こう回します」と説明できる形になりました。',
      stats: '加算の取得まで到達'
    },
    {
      facility: '児童発達支援 B事業所',
      role: '施設長',
      title: 'モニタリングの期限を、自分の記憶に頼っていました',
      comment: '児発管が私一人なので、誰もチェックしてくれないんですよね。以前ヒヤッとしたことがあって、それからは付箋とカレンダーで管理していました。近づいたら出てくるというだけなんですが、その不安がなくなったのが一番大きいです。',
      stats: '未作成減算を回避'
    },
    {
      facility: '放課後等デイサービス C教室',
      role: '児童発達支援管理責任者',
      title: '計画書を家に持ち帰らなくなりました',
      comment: '更新が重なる月は、閉所後に残って21時、22時まで文章を考えていました。いまは下書きがあるので、私がやるのは「この子はここが違う」と直すところだけです。ゼロから書くのと直すのとでは、終わったあとの疲れ方が違います。',
      stats: '作成業務の時間を短縮'
    },
    {
      facility: '多機能型事業所 Dセンター',
      role: '代表取締役',
      title: '請求まで変えるなら、導入していないと思います',
      comment: '職員がいまの請求ソフトにやっと慣れたところで、また覚え直しはさせられません。説明を聞いたときに「請求は触りません」と言われて、それなら並べて使えるなと。実際、経理担当の仕事は何も変わっていません。',
      stats: '乗り換えコストなし'
    },
    {
      facility: '児童発達支援 Eルーム',
      role: '児童発達支援管理責任者',
      title: '前任者の計画書を見て、書き方を想像していました',
      comment: '引き継ぎのときに書式はもらったのですが、「どこまで書くか」が人によって違って戸惑いました。いまは同じ観点で並ぶので、新しく入った職員にも現物を見せて「この形で」と言えます。',
      stats: '属人化の解消'
    },
    {
      facility: '放課後等デイサービス F施設',
      role: '管理者',
      title: '実地指導の通知が来てから慌てる側でした',
      comment: '支援プログラムも自己評価も、やらなければいけないのは分かっているんです。ただ日々の業務が優先されて、気づくと一年経っている。何をいつまでにやるのかが一覧で見えるだけでも、ずいぶん違います。',
      stats: '公表関係の減算対策'
    },
    {
      facility: '児童発達支援 G事業所',
      role: '児童指導員',
      title: 'メモを清書する時間が、そもそもなくなりました',
      comment: '送迎から戻って、手書きのメモをパソコンに打ち直す。これが地味にきつかったです。いまは話しておけば形になっているので、記録が溜まらなくなりました。そのまま出せるわけではないので、多少は直しています。',
      stats: '記録を溜めなくなった'
    },
    {
      facility: '放課後等デイサービス H教室',
      role: '施設長',
      title: '報告書が面倒で、加算を見送っていた時期があります',
      comment: '不登校のお子さんへの支援自体はやっていました。ただ学校に出す報告書をまとめる時間が取れず、算定しないまま過ぎた月が何度かあります。いま思えばもったいないことをしていました。',
      stats: '加算算定のハードル低下'
    }
  ];

  return (
    <Box
      component="section"
      sx={{
        py: SECTION_PY,
        // 背景はファーストビュー（LandingTop）と同じ淡いグラデーションに揃えている
        background: `linear-gradient(180deg, #ffffff 0%, #f5fbfb 45%, ${alpha(primaryColor, 0.07)} 100%)`,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* --- 背景装飾：ファーストビュー（LandingTop）と同じブランドカラーの曲線 --- */}
      <Box
        component="svg"
        aria-hidden="true"
        viewBox="0 0 1440 900"
        preserveAspectRatio="xMidYMid slice"
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          zIndex: 0,
          pointerEvents: 'none'
        }}
      >
        <path
          d="M -120 700 C 220 700 250 250 520 250 C 760 250 780 560 1020 560 C 1260 560 1300 240 1560 240"
          fill="none"
          stroke={alpha(primaryColor, 0.16)}
          strokeWidth="46"
          strokeLinecap="round"
        />
        <path
          d="M -120 810 C 260 810 300 380 600 380 C 860 380 880 660 1120 660 C 1340 660 1380 380 1560 380"
          fill="none"
          stroke={alpha(primaryColor, 0.08)}
          strokeWidth="26"
          strokeLinecap="round"
        />
      </Box>

      <Container sx={{ position: 'relative', zIndex: 1 }}>
        {/* 見出し：delay 0.7 */}
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.7 }}
          >
            <Typography
              variant="h2"
              component="h2"
              sx={{ 
                fontWeight: 900, 
                fontSize: { xs: '2rem', md: '3rem' }, 
                color: '#1a1a1a', 
                mb: 2,
                position: 'relative',
                display: 'inline-block'
              }}
            >
              導入事例
              <Box
                sx={{
                  width: '40%',
                  height: '4px',
                  bgcolor: primaryColor,
                  borderRadius: '2px',
                  position: 'absolute',
                  bottom: -8,
                  left: '30%'
                }}
              />
            </Typography>
          </motion.div>
        </Box>

        {/* スクロール誘導テキスト */}
        <Stack 
          direction="row" 
          spacing={1} 
          alignItems="center" 
          justifyContent="flex-end"
          sx={{ mb: 2, opacity: 0.6, px: { xs: 2, md: 0 } }}
        >
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.05em' }}>
            SCROLL
          </Typography>
          <ArrowForwardIcon sx={{ fontSize: '0.9rem' }} />
        </Stack>

        {/* スクロールエリア */}
        <Box
          ref={scrollRef}
          sx={{
            display: 'flex',
            overflowX: 'auto',
            gap: { xs: 2, md: 3 },
            pb: 6,
            px: { xs: 2, md: 0 },
            scrollSnapType: 'x mandatory',
            '&::-webkit-scrollbar': { display: 'none' },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {voiceData.map((item, index) => (
            <Box
              key={index}
              component={motion.div}
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.7 + index * 0.1 }}
              sx={{
                flex: '0 0 auto',
                width: { xs: '85%', md: '400px' }, // 8枚並ぶため少し幅をスリムに調整
                scrollSnapAlign: { xs: 'center', md: 'start' }
              }}
            >
              <Box
                component="article"
                sx={{
                  p: { xs: 4, md: 4.5 },
                  borderRadius: '32px',
                  bgcolor: alpha('#fff', 0.85),
                  backdropFilter: 'blur(12px)',
                  border: `1px solid ${alpha('#fff', 0.8)}`,
                  boxShadow: `0 15px 35px -10px ${alpha('#000', 0.05)}`,
                  position: 'relative',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 25px 50px -12px ${alpha(primaryColor, 0.15)}`,
                    borderColor: alpha(primaryColor, 0.3)
                  }
                }}
              >
                <RequestQuoteIcon
                  sx={{
                    fontSize: '2rem',
                    color: alpha(primaryColor, 0.1),
                    position: 'absolute',
                    top: 20,
                    right: 24
                  }}
                />

                <Stack spacing={2.5} sx={{ height: '100%' }}>
                  <Box>
                    <Typography
                      component="span"
                      sx={{
                        bgcolor: alpha(primaryColor, 0.1),
                        color: primaryColor,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        fontWeight: 800,
                      }}
                    >
                      {item.stats}
                    </Typography>
                  </Box>

                  <Typography
                    variant="h3"
                    component="h3"
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: '1.15rem', md: '1.25rem' },
                      color: '#1a1a1a',
                      lineHeight: 1.4
                    }}
                  >
                    「{item.title}」
                  </Typography>

                  <Typography
                    component="p"
                    sx={{ color: '#4b5563', fontSize: '0.9rem', lineHeight: 1.7, flexGrow: 1 }}
                  >
                    {item.comment}
                  </Typography>

                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{ pt: 2.5, borderTop: `1px solid ${alpha(primaryColor, 0.1)}` }}
                  >
                    <Avatar sx={{ bgcolor: alpha(primaryColor, 0.1), color: primaryColor, width: 38, height: 38, fontWeight: 900, fontSize: '0.9rem' }}>
                      {item.facility.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 800, color: '#1a1a1a', fontSize: '0.85rem' }}>
                        {item.facility}
                      </Typography>
                      <Typography sx={{ color: '#6b7280', fontSize: '0.75rem', fontWeight: 500 }}>
                        {item.role}
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>
              </Box>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default VoiceSection;